import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth } from '../config/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { handleUserLogin, setUltraAuthenticatedStatus as updateUserUltraAuthInFirestore } from '../services/userService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null); // Firebase auth user object
  const [firestoreUser, setFirestoreUser] = useState(null); // Custom user profile from Firestore
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        try {
          // Fetches or creates user profile in Firestore upon Firebase auth state change.
          const userProfile = await handleUserLogin(user);
          setFirestoreUser(userProfile);
        } catch (error) {
          console.error("AuthContext: Error handling user login or fetching profile:", error);
          setFirestoreUser(null); // Clear profile on error to prevent inconsistent state
        }
      } else {
        setFirestoreUser(null); // Clear Firestore user profile on logout
      }
      setLoading(false);
    });

    return unsubscribe; // Cleanup Firebase auth listener on unmount
  }, []);

  /**
   * Updates the user's ultra authentication status in Firestore and local state.
   * @param {boolean} isAuthenticated - The new ultra authentication status.
   */
  const setUltraPasswordAuthenticated = async (isAuthenticated) => {
    if (!currentUser || !currentUser.uid) {
      console.error("AuthContext: Cannot set ultra password status - no current user.");
      throw new Error("User not available to update authentication status.");
    }
    try {
      await updateUserUltraAuthInFirestore(currentUser.uid, isAuthenticated);
      // Refresh firestoreUser state to reflect the change immediately
      setFirestoreUser(prevUser => {
        // It's possible prevUser is null if initial load failed or user logged out then back in quickly
        if (prevUser) {
          return { ...prevUser, authenticated: isAuthenticated };
        }
        // If prevUser is null, this indicates a potential issue or a need to refetch the full profile.
        // However, for this specific action, we know the UID and the new auth status.
        // A more robust solution might refetch the user profile here if prevUser is null.
        console.warn("AuthContext: firestoreUser was null when updating ultra auth status. Partial update applied.")
        return { uid: currentUser.uid, email: currentUser.email, authenticated: isAuthenticated }; 
      });
    } catch (error) {
      console.error("AuthContext: Error updating ultra password status:", error);
      // Potentially re-throw or set an error state for UI feedback
      throw new Error("Failed to update authentication status.");
    }
  };

  const value = {
    currentUser,
    firestoreUser,
    loading,
    isUltraAuth: firestoreUser?.authenticated || false, // Derived from firestoreUser for convenience
    setUltraPasswordAuthenticated,
  };

  return (
    <AuthContext.Provider value={value}>
      {/* Render children only after initial auth state check is complete */}
      {!loading && children}
    </AuthContext.Provider>
  );
};
